import { useState } from 'react';
// @mui
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
// components
import MyAvatar from 'components/MyAvatar';
import MenuPopover from 'components/MenuPopover';
// icons
import LogoutIcon from 'assets/icon/logout';
import UserIcon from 'assets/icon/user';
import ChevronDown from 'assets/icon/chevron/ChevronDown';
import ChevronUpIcon from 'assets/icon/chevron/up';
// other imports
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PostAsync } from 'common/httpRequests';
import { BACKEND } from 'constants/settings';
import { NavBarAccountMenuItem } from './NavBarAccountMenuItem';

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
}));

interface INavbarAccountProps {
  isCollapse: boolean | undefined;
}

export function NavbarAccount({ isCollapse }: INavbarAccountProps) {
  const customer_ = useSelector((state: any) => state.customer);
  const customer = customer_?.customer;
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  function handleLogout() {
    const customerId = localStorage.getItem('customerId');
    PostAsync(`${BACKEND}/api/customerservice/Customer/logout/${customerId}`, {});
  }

  const onClose = () => {
    setAnchorEl(null);
  };

  return (
    <RootStyle>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
      >
        <Box display="flex">
          <MyAvatar user={{ name: customer?.name }} sx={{ width: 32, height: 32 }} />
          <Box
            sx={{
              ml: 2,
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
              ...(isCollapse && { ml: 0, width: 0, display: 'none' }),
            }}
          >
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                maxWidth: 140,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {customer?.name}
            </Typography>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                color: 'text.secondary',
                maxWidth: 140,
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {customer?.email}
            </Typography>
          </Box>
        </Box>
        {!isCollapse && (
          <Box
            sx={{
              position: 'relative',
              my: 'auto',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            {anchorEl === null ? (
              <ChevronDown height={20} width={20} />
            ) : (
              <ChevronUpIcon height={20} width={20} />
            )}
          </Box>
        )}
      </Box>
      <MenuPopover
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 0, horizontal: 0 }}
        arrow="right-top"
        sx={{
          backgroundColor: theme.palette.background.neutral,
          mt: -1,
          width: 250,
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <NavBarAccountMenuItem
            title={t('My profile')}
            startIcon={<UserIcon height={18} width={18} />}
            onClick={() => {
              navigate(`/${window.location.pathname.split('/')[1]}/account`);
              onClose();
            }}
          />
          <NavBarAccountMenuItem
            title={t('Logout')}
            startIcon={<LogoutIcon height={18} width={18} />}
            onClick={() => {
              handleLogout();
              onClose();
            }}
          />
        </Box>
      </MenuPopover>
    </RootStyle>
  );
}
